/* eslint-disable func-names */
/* eslint-disable prefer-arrow-callback */
import React, { useState } from 'react';
import { FormattedMessage, intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import { prop } from 'ramda';
import { useMutation, useQuery } from '@apollo/client';

import { hotelsShape } from 'constants/propTypes';
import { HOTEL_STATUSES } from 'constants/booking';
import { getHotelsStatuses } from 'utils/booking';
import { GET_HOTEL_BOOKINGS_INFO, HOTEL_BOOKING } from 'store/queries';
import {
  CANCEL_HOTEL_JACTRAVEL,
  ADJUST_POINTS_ENHANCEMENT,
  ADD_NOTE_TO_BOOKING,
  UPDATE_SALESFORCE_STAFF_VOUCHERS_FOR_BOOKING,
  UPDATE_SALESFORCE_BOOKING_STATUS,
  CANCEL_HOTEL_BOOKING,
  ROLLBACK_LOYALTY_ACTIVITY
} from 'store/mutations';
import { ConfirmButton, CancelButton } from 'components/ui/Button';
import Modal from 'components/ui/Modal';
import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import Toast from 'components/ui/Toast';

import { confirmCancelConfirmedBookingMutation } from './mutations';

const ConfirmCancellationCta = ({
  hotels = [],
  allowPointsWriteOff = false,
  skipConfirmation,
  markPaymentAsProcessed,
  bookingReference,
  errorCTARedirect,
  nextRoute,
  history: { push },
  intl: { formatMessage },
  user: { paymentLocation, username },
  label,
  totalPoints,
  milesUsed,
  membershipNumber,
  currency,
  handleSubmit,
  cancelHotelInJacTravel,
  userTierBenefitUpgrades,
  userBusinessBenefitUpgrades
}) => {
  const isDotwBooking = hotels[0]?.isDotwBooking;

  const [cancelHotelError, setCancelHotelError] = useState(false);
  const [showPointsWriteOffWarning, setShowPointsWriteOffWarning] = useState(false);
  const { data: hotelsData } = useQuery(isDotwBooking ? HOTEL_BOOKING : GET_HOTEL_BOOKINGS_INFO, {
    variables: isDotwBooking ? { reference: bookingReference } : { bookingReference }
  });
  const [confirmCancellation, { error, loading }] = useMutation(confirmCancelConfirmedBookingMutation, {
    variables: {
      bookingReference,
      paymentLocation: prop('name', paymentLocation),
      processRefund: markPaymentAsProcessed,
      currency
    }
  });

  const [cancelHotel, { loading: cancelHotelJacTravelLoading }] = useMutation(
    isDotwBooking ? CANCEL_HOTEL_BOOKING : CANCEL_HOTEL_JACTRAVEL,
    {
      variables: isDotwBooking
        ? { options: { reference: bookingReference } }
        : {
            bookingReference
          }
    }
  );

  const comments = `Booking Reference: '${bookingReference}'. Refund in Voyager by ${username}.`;

  const [adjustLoyaltyPoints, { loading: adjustLoyaltyPointsLoading, error: adjustLoyaltyPointsError }] = useMutation(
    ADJUST_POINTS_ENHANCEMENT,
    {
      variables: {
        membershipNumber,
        amountRewardPoints: totalPoints,
        amountAcceleratorPoints: 0,
        amountStatusPoints: 0,
        reasonCode: 'Refunded Redemption',
        reasonText: comments,
        comments,
        actionBy: username,
        pnr: bookingReference
      }
    }
  );

  const [addNote, { loading: addNoteLoading }] = useMutation(ADD_NOTE_TO_BOOKING);

  const [updateSalesforceStaffVouchers, { loading: updateSalesforceStaffVouchersLoading }] = useMutation(
    UPDATE_SALESFORCE_STAFF_VOUCHERS_FOR_BOOKING,
    {
      variables: {
        bookingReference,
        vouchersUsed: milesUsed
      }
    }
  );

  const [updateSalesforceBookingStatus, { loading: updateSalesforceBookingStatusLoading }] = useMutation(UPDATE_SALESFORCE_BOOKING_STATUS, {
    variables: {
      bookingReference,
      status: 'CANCELLED'
    }
  });

  const [rollbackLoyaltyActivity, { loading: rollbackLoyaltyActivityLoading }] = useMutation(ROLLBACK_LOYALTY_ACTIVITY);

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect
    });
  }

  if (adjustLoyaltyPointsError) {
    throw new AgentPortalError(adjustLoyaltyPointsError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect,
      ctaText: 'errorBoundary.continue',
      additionalMessage: <FormattedMessage id="clubEurostar.adjustPointsFailedMessage" values={{ points: totalPoints }} />
    });
  }

  if (cancelHotelError) {
    throw new Error('Sorry, we were unable to cancel this booking on a third party provider at the moment, please try again later');
  }

  const hotelStatuses =
    (hotelsData?.getHotelBookings || hotelsData?.hotelBooking) &&
    getHotelsStatuses(hotels, isDotwBooking ? hotelsData.hotelBooking : hotelsData.getHotelBookings, isDotwBooking);
  const isHotelCancelledOrNotFoundInJacTravel =
    hotelStatuses?.length &&
    hotelStatuses.find((h) => h.jacTravelStatus === HOTEL_STATUSES.CANCELLED || h.jacTravelStatus === HOTEL_STATUSES.NOT_FOUND);
  const isLoading =
    loading ||
    cancelHotelJacTravelLoading ||
    adjustLoyaltyPointsLoading ||
    addNoteLoading ||
    updateSalesforceStaffVouchersLoading ||
    updateSalesforceBookingStatusLoading ||
    rollbackLoyaltyActivityLoading;
  const [nextRoutePath, nextRouteQuery] = nextRoute.split('?');
  const nextRouteQueryString = new URLSearchParams(nextRouteQuery);
  nextRouteQueryString.set('currency', currency);
  const routeWithCurrency = `${nextRoutePath}?${nextRouteQueryString}`;

  const rollbackTierBenefitUpgrades = async (benefits = []) => {
    if (benefits?.length > 0) {
      const response = await Promise.allSettled(
        benefits.map(({ transactionJournal }) =>
          rollbackLoyaltyActivity({
            variables: { activityId: transactionJournal, membershipNumber, actionBy: username }
          })
        )
      );

      await addNote({
        variables: {
          note: `${response.reduce(
            (acc, { status }) => (status === 'fulfilled' ? acc + 1 : acc),
            0
          )} tier benefit upgrade(s) refunded for Club Eurostar account ${membershipNumber}`,
          bookingReference
        }
      });
    }
  };

  const rollbackBusinessBenefitUpgrades = async (benefits = []) => {
    if (benefits?.length > 0) {
      const response = await Promise.allSettled(
        benefits.map(({ transactionJournal }) =>
          rollbackLoyaltyActivity({
            variables: { activityId: transactionJournal, membershipNumber, actionBy: username }
          })
        )
      );

      await addNote({
        variables: {
          note: `${response.reduce(
            (acc, { status }) => (status === 'fulfilled' ? acc + 1 : acc),
            0
          )} business account benefit upgrade(s) refunded for Club Eurostar account ${membershipNumber}`,
          bookingReference
        }
      });
    }
  };

  const handleSubmitSkipConfirmation = async () => {
    if (cancelHotelInJacTravel && !isHotelCancelledOrNotFoundInJacTravel) {
      try {
        const result = await cancelHotel();

        if (!result || (!isDotwBooking && !result.data.refund.success)) {
          return setCancelHotelError(true);
        }
      } catch (err) {
        return setCancelHotelError(true);
      }
    }

    await rollbackTierBenefitUpgrades(userTierBenefitUpgrades);
    await rollbackBusinessBenefitUpgrades(userBusinessBenefitUpgrades);

    return push(routeWithCurrency);
  };

  if (skipConfirmation) {
    return (
      <ConfirmButton
        onClick={handleSubmit(function () {
          return handleSubmitSkipConfirmation();
        })}
        data-cy="take-payment-cta"
        label={label}
      />
    );
  }

  const handleConfirm = async (createToast, refundPoints = true) => {
    if (cancelHotelInJacTravel && !isHotelCancelledOrNotFoundInJacTravel) {
      try {
        const result = await cancelHotel();

        if (!result || (!isDotwBooking && !result.data.refund.success)) {
          return setCancelHotelError(true);
        }
      } catch (err) {
        return setCancelHotelError(true);
      }
    }

    const { data } = await confirmCancellation();
    const isCancellable = data?.confirmCancelBookingItems?.cancellable;
    if (!isCancellable) {
      createToast(
        formatMessage({
          id: 'manageBooking.cannotProcessRefunds'
        })
      );

      return push(errorCTARedirect);
    }

    await rollbackTierBenefitUpgrades(userTierBenefitUpgrades);
    await rollbackBusinessBenefitUpgrades(userBusinessBenefitUpgrades);

    if (totalPoints || !refundPoints) {
      if (refundPoints) {
        await adjustLoyaltyPoints();
      }
      await addNote({
        variables: {
          note: `Refund of tickets${refundPoints ? '' : ' without a refund'} for Club Eurostar account: ${membershipNumber},
     reward points : ${refundPoints ? totalPoints : 0}, accelerator points : 0, status points : 0.`,
          bookingReference
        }
      });
    }

    if (milesUsed) {
      if (data?.confirmCancelBookingItems?.status === 'CANCELLED') {
        await updateSalesforceBookingStatus();
      }

      await updateSalesforceStaffVouchers();
    }

    return push(routeWithCurrency);
  };

  return (
    <Toast>
      {({ createToast }) => (
        <>
          <ConfirmButton
            id="confirm-cancel-booking-items-cta"
            loading={isLoading}
            disabled={isLoading}
            data-cy="confirm-cta"
            onClick={handleSubmit(function () {
              return handleConfirm(createToast);
            })}
            variant="primary"
          >
            {label}
          </ConfirmButton>
          {allowPointsWriteOff && (
            <>
              <ConfirmButton
                id="confirm-cancel-without-refund-cta"
                data-cy="confirm-without-refund-cta"
                disabled={isLoading}
                onClick={handleSubmit(function () {
                  setShowPointsWriteOffWarning(true);
                })}
                variant="secondary"
              >
                <FormattedMessage id="refunds.confirmCancellationWithoutRefund" />
              </ConfirmButton>
              <Modal
                isOpen={showPointsWriteOffWarning}
                onClose={isLoading ? undefined : () => setShowPointsWriteOffWarning(false)}
                title={<FormattedMessage id="refunds.loyaltyBooking" />}
                bottomRow={
                  <>
                    <ConfirmButton
                      data-cy="confirm-points-write-off-cta"
                      loading={isLoading}
                      disabled={isLoading}
                      onClick={handleSubmit(function () {
                        return handleConfirm(createToast, false);
                      })}
                      variant="primary"
                    >
                      <FormattedMessage id="refunds.confirmCancellation" />
                    </ConfirmButton>
                    <CancelButton disabled={isLoading} onClick={() => setShowPointsWriteOffWarning(false)} variant="secondary">
                      <FormattedMessage id="refunds.selectDifferentOption" />
                    </CancelButton>
                  </>
                }
              >
                <p>
                  <FormattedMessage id="refunds.pointsWriteOffWarning" values={{ points: totalPoints }} />
                </p>
                <p>
                  <FormattedMessage id="refunds.confirmProceed" />
                </p>
              </Modal>
            </>
          )}
        </>
      )}
    </Toast>
  );
};

ConfirmCancellationCta.propTypes = {
  allowPointsWriteOff: PropTypes.bool,
  intl: intlShape,
  label: PropTypes.node,
  skipConfirmation: PropTypes.bool.isRequired,
  errorCTARedirect: PropTypes.string.isRequired,
  nextRoute: PropTypes.string.isRequired,
  bookingReference: PropTypes.string.isRequired,
  markPaymentAsProcessed: PropTypes.bool.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  user: PropTypes.shape({
    paymentLocation: PropTypes.shape({
      name: PropTypes.string
    }),
    username: PropTypes.string
  }),
  currency: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  cancelHotelInJacTravel: PropTypes.bool,
  totalPoints: PropTypes.number,
  milesUsed: PropTypes.number,
  membershipNumber: PropTypes.string,
  hotels: hotelsShape,
  userTierBenefitUpgrades: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      benefitStatus: PropTypes.string,
      benefitName: PropTypes.string,
      transactionJournal: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      cancellationReason: PropTypes.oneOfType([PropTypes.string, PropTypes.objectOf(null)]),
      bookingReference: PropTypes.string,
      benefitRedemptionDate: PropTypes.string
    })
  ),
  userBusinessBenefitUpgrades: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      benefitStatus: PropTypes.string,
      benefitName: PropTypes.string,
      benefitTypeName: PropTypes.string,
      transactionJournal: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      reasonForCancellation: PropTypes.oneOfType([PropTypes.string, PropTypes.objectOf(null)]),
      pnr: PropTypes.string,
      benefitRedemptionDate: PropTypes.string
    })
  )
};

export default ConfirmCancellationCta;
